import React, {
  useMemo, useCallback, ReactElement, Fragment,
} from 'react';
import Widget from '../../../components/widget/widget.component';
import HintLinked from '../../../components/hint-linked/hint-linked.component';
import Table from '../../../components/table/table.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import FormFieldText from '../../../components/form-field-text/form-field-text.component';
import FormFieldNumber from '../../../components/form-field-number/form-field-number.component';
import FormFieldDate from '../../../components/form-field-date/form-field-date.component';
import FormFieldSelect from '../../../components/form-field-select/form-field-select.component';
import FormFieldCheckbox from '../../../components/form-field-checkbox/form-field-checkbox.component';
import MenuItemList from '../../../components/menu-item-list/menu-item-list.component';
import FormService from '../../../services/form-service';
import { DEAL_COMMON_EDIT } from '../../../constants/entity/permissions';
import { REPORT_DEAL_CUSTOM_SECTION_FIELD_MAP } from '../../../constants/entity/report-type-lists';
import { REPORT_TYPE_DEAL_BUILD_TO_SUIT_PIPELINE_SUMMARY } from '../../../constants/entity/report-types';
import {
  FIELD_TYPE_TEXT, FIELD_TYPE_TEXT_MULTILINE, FIELD_TYPE_NUMBER, FIELD_TYPE_NUMBER_CURRENCY, FIELD_TYPE_NUMBER_DOUBLE,
  FIELD_TYPE_NUMBER_PERCENT, FIELD_TYPE_DATE, FIELD_TYPE_DROPDOWN, FIELD_TYPE_BOOLEAN,
} from '../../../constants/entity/field-types';
import {
  FIELD_LIST_TYPE_MEASURE_UNIT, FIELD_LIST_TYPE_PRICING_TYPE,
} from '../../../constants/entity/field-list-types';

const DealCustomSection: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement | boolean => {
  const WIDGET_NAME_PREFIX: string = 'widget-deal-custom-section';

  const {
    isEditMode,
    isNewDeal,
    dealCustomSectionData,
    pricingTypeList,
    measureUnitList,
    dealCustomListTypeList,
    onEditModeButtonClick,
  } = props;

  const dealCommonDataEditPermissions: string[] = useMemo((): string[] => [DEAL_COMMON_EDIT], []);

  const formPrefix: string = 'dealData.dealCustomSectionsData';
  const formKeyDealCustomSectionItem: string = FormService.getIndexedFormFieldKey(dealCustomSectionData.id);
  const allItemsEmpty: boolean = dealCustomSectionData && dealCustomSectionData.fields.length
    ? dealCustomSectionData.fields.reduce((
      result: boolean, dealCustomSectionFieldItem: DealSectionField,
    ): boolean => (
      result && !dealCustomSectionFieldItem.valueText && !dealCustomSectionFieldItem.value
    ), true) : true;
  const isEmpty: boolean = !isEditMode && !isNewDeal && allItemsEmpty;
  const widgetName: string = `${WIDGET_NAME_PREFIX}-${dealCustomSectionData.id}`;

  const checkIfDealCustomSectionFieldUsed: Function = useCallback((
    dealCustomSectionName: string, dealCustomSectionFieldName: string,
  ): boolean => {
    return !!REPORT_DEAL_CUSTOM_SECTION_FIELD_MAP[REPORT_TYPE_DEAL_BUILD_TO_SUIT_PIPELINE_SUMMARY].find(
      ([reportDealCustomSectionName, reportDealCustomSectionFieldName]: [string, string]): boolean => (
        reportDealCustomSectionName === dealCustomSectionName
        && reportDealCustomSectionFieldName === dealCustomSectionFieldName
      ),
    );
  }, []);

  if (isEmpty) {
    return false;
  }

  return (
    <Widget
      isFullWidth
      isFullHeight
      isEditMode={isEditMode}
      withEditMode={!isNewDeal}
      editPermissions={dealCommonDataEditPermissions}
      widgetName={widgetName}
      defaultWidgetTitle={dealCustomSectionData.name}
      onEditModeButtonClick={onEditModeButtonClick}
    >
      {
        dealCustomSectionData.fields && !!dealCustomSectionData.fields.length && (
          <Table>
            <TableBody isPlain withZebraRows withTwoColumns>
              {
                dealCustomSectionData.fields.map((
                  dealCustomSectionFieldData: DealSectionField,
                ): ReactElement | boolean => {
                  if (!isEditMode && !isNewDeal && !dealCustomSectionFieldData.value
                    && !dealCustomSectionFieldData.valueText) {
                    return false;
                  }

                  const formKeyDealCustomSectionFieldItem: string = FormService.getIndexedFormFieldKey(
                    dealCustomSectionFieldData.id,
                  );
                  const fieldNamePrefix: string = `${formPrefix}[${formKeyDealCustomSectionItem}].`
                    + `fields[${formKeyDealCustomSectionFieldItem}]`;
                  const fieldPlaceholder: string = 'Value';
                  const isTextField: boolean = dealCustomSectionFieldData.type === FIELD_TYPE_TEXT;
                  const isTextMultilineField: boolean = dealCustomSectionFieldData.type === FIELD_TYPE_TEXT_MULTILINE;
                  const isNumberField: boolean = dealCustomSectionFieldData.type === FIELD_TYPE_NUMBER;
                  const isNumberDoubleField: boolean = dealCustomSectionFieldData.type === FIELD_TYPE_NUMBER_DOUBLE;
                  const isNumberPercentField: boolean = dealCustomSectionFieldData.type === FIELD_TYPE_NUMBER_PERCENT;
                  const isNumberCurrencyField: boolean = dealCustomSectionFieldData.type === FIELD_TYPE_NUMBER_CURRENCY;
                  const isDateField: boolean = dealCustomSectionFieldData.type === FIELD_TYPE_DATE;
                  const isDropdownField: boolean = dealCustomSectionFieldData.type === FIELD_TYPE_DROPDOWN;
                  const isBooleanField: boolean = dealCustomSectionFieldData.type === FIELD_TYPE_BOOLEAN;

                  let dealCustomSectionFieldValueList: StructureItem[] = [];
                  if (isDropdownField) {
                    if (dealCustomSectionFieldData.listType === FIELD_LIST_TYPE_MEASURE_UNIT) {
                      dealCustomSectionFieldValueList = measureUnitList;
                    } else if (dealCustomSectionFieldData.listType === FIELD_LIST_TYPE_PRICING_TYPE) {
                      dealCustomSectionFieldValueList = pricingTypeList;
                    } else {
                      const dealCustomListType: FieldListType | null = (
                        dealCustomListTypeList && dealCustomListTypeList.length
                          ? dealCustomListTypeList.find((dealCustomListTypeItem: FieldListType): boolean => (
                            dealCustomListTypeItem.name === dealCustomSectionFieldData.listType
                          )) : null
                      );

                      if (dealCustomListType && dealCustomListType.options && dealCustomListType.options.length) {
                        dealCustomSectionFieldValueList = dealCustomListType.options;
                      }
                    }
                  }

                  const menuItemDealCustomSectionFieldValueList: ReactElement[] = MenuItemList({
                    optionItemValueKey: 'name',
                    optionList: dealCustomSectionFieldValueList,
                  });

                  return (
                    <TableRow key={dealCustomSectionFieldData.id}>
                      <TableCell>
                        {dealCustomSectionFieldData.name}
                        {
                          checkIfDealCustomSectionFieldUsed(
                            dealCustomSectionData.name, dealCustomSectionFieldData.name,
                          ) && (
                            <HintLinked title="This deal field is linked to report" />
                          )
                        }
                      </TableCell>
                      <TableCell isTextMultiline={!isEditMode && !isNewDeal && isTextMultilineField}>
                        {
                          isEditMode || isNewDeal ? (
                            <Fragment>
                              <FormFieldText isHidden name={`${fieldNamePrefix}.id`} />
                              <FormFieldText
                                isPlain
                                isHidden
                                name={`${fieldNamePrefix}.name`}
                                value={dealCustomSectionFieldData.name}
                              />
                              <FormFieldText
                                isPlain
                                isHidden
                                name={`${fieldNamePrefix}.type`}
                                value={dealCustomSectionFieldData.type}
                              />
                              {
                                (isTextField || isTextMultilineField) && (
                                  <FormFieldText
                                    isInline={isTextMultilineField}
                                    isMultiline={isTextMultilineField}
                                    name={`${fieldNamePrefix}.value`}
                                    placeholder={fieldPlaceholder}
                                    maxRows={isTextMultilineField ? FormService.TEXTAREA_MAX_ROWS_SMALL : undefined}
                                    minRows={isTextMultilineField ? FormService.TEXTAREA_MIN_ROWS_DEFAULT : undefined}
                                  />
                                )
                              }
                              {
                                (isNumberField || isNumberDoubleField || isNumberPercentField
                                  || isNumberCurrencyField) && (
                                  <FormFieldNumber
                                    isFloat={isNumberDoubleField || isNumberPercentField || isNumberCurrencyField}
                                    isPercent={isNumberPercentField}
                                    withNoLimit={isNumberPercentField}
                                    name={`${fieldNamePrefix}.value`}
                                    placeholder={fieldPlaceholder}
                                  />
                                )
                              }
                              {
                                isDateField && (
                                  <FormFieldDate
                                    withBlankValues
                                    withNoUnderline
                                    name={`${fieldNamePrefix}.value`}
                                  />
                                )
                              }
                              {
                                isDropdownField && (
                                  <FormFieldSelect
                                    withBlankElement
                                    isDisabled={(
                                      dealCustomSectionFieldValueList && !dealCustomSectionFieldValueList.length
                                    )}
                                    name={`${fieldNamePrefix}.value`}
                                    optionsTotal={(
                                      dealCustomSectionFieldValueList && dealCustomSectionFieldValueList.length
                                    )}
                                  >
                                    {menuItemDealCustomSectionFieldValueList}
                                  </FormFieldSelect>
                                )
                              }
                              {
                                isBooleanField && (
                                  <FormFieldCheckbox name={`${fieldNamePrefix}.value`} />
                                )
                              }
                            </Fragment>
                          ) : (
                            dealCustomSectionFieldData.valueText
                          )
                        }
                      </TableCell>
                    </TableRow>
                  );
                })
              }
            </TableBody>
          </Table>
        )
      }
    </Widget>
  );
});

export default DealCustomSection;
